// Library Methods
import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";

// Components
import Grid from "@mui/material/Grid";
import Alert from "src/components/shared/alerts/Alert/Alert";
import GazeGraph from "../../../GazeGraph/GazeGraph";
import VFScreeningChartLegend from "./VFScreeningChartLegend/VFScreeningChartLegend";
import VFScreeningChart from "./VFScreeningChart/VFScreeningChart";

// Contexts
import { ReportContext } from "src/contexts/reports/ReportContext";
import { ReportSectionContext } from "src/contexts/reports/ReportSectionContext";

// Utils
import {
  getChartSpots,
  getGazeChartData,
  isTerminatedExamReport,
} from "src/utils/exams/examReportsHelper";

// Constants
import { examCardEnums } from "src/constants/enums";

// Hooks
import { useComponentDimensions } from "src/hooks/window/useComponentDimensions";

const VFScreeningGraph = () => {
  const { t } = useTranslation();

  const { exam } = useContext(ReportContext);
  const { sectionId, eyeType, examReport } = useContext(ReportSectionContext);

  const isTerminated = useMemo(
    () =>
      isTerminatedExamReport({
        exam,
        examReport,
        sectionName: examCardEnums.VISUAL_FIELD_SCREENING.sectionName,
      }),
    [exam, examReport]
  );

  const vfScreeningSpots = useMemo(
    () => getChartSpots(examReport) ?? [],
    [examReport]
  );

  const gazeSpots = useMemo(() => {
    const { gazeSpots } = getGazeChartData(examReport);
    return gazeSpots ?? [];
  }, [examReport]);

  const dimensions = useComponentDimensions({
    componentId: "#vfscreening-container",
  });

  return (
    <Grid container gap="20px" id="vfscreening-container">
      {isTerminated && (
        <Alert
          variant="error"
          alertContent={t("Terminated Exam")}
          props={{ width: "100%" }}
        />
      )}

      <Grid
        id={`eye-report-${sectionId}-${eyeType}`}
        item
        xs={12}
        display="flex"
        justifyContent="center"
        alignItems="center"
        mt="20px"
      >
        <VFScreeningChart
          vfScreeningSpots={vfScreeningSpots}
          eyeType={eyeType}
          gridType={examReport?.exam?.grid}
          containerSize={dimensions?.dynamicWidth}
        />
      </Grid>
      <Grid item xs={12}>
        <VFScreeningChartLegend vfScreeningSpots={vfScreeningSpots} />
      </Grid>
      <Grid item xs={12}>
        <GazeGraph gazeSpots={gazeSpots} />
      </Grid>
    </Grid>
  );
};

export default VFScreeningGraph;
